'use client';

import { useEffect } from 'react';

import i18n from '@sola/locales/index.ts';

/**
 * Warm the i18next store with additional translation namespaces that a page's
 * interaction-mounted UI (modals/sheets) will need.
 *
 * Namespaces are lazy-loaded on first use. When a namespace is only referenced
 * inside a modal/sheet, it isn't fetched until the user opens it — at which
 * point the component suspends mid-interaction. Preloading here, while the
 * hosting page is idle, means the namespace is already in the store by the
 * time the modal opens, so it renders instantly.
 *
 * Fire-and-forget: this never suspends the caller.
 */
export const usePreloadNamespaces = (...namespaces: string[]) => {
  // Stable primitive dependency so the effect only re-runs if the actual set
  // of namespaces changes (which it never does for a given call site), while
  // still calling with the original array.
  const key = namespaces.join(',');

  useEffect(() => {
    if (!namespaces.length) {
      return;
    }
    void i18n.loadNamespaces(namespaces);
  }, [key]);
};
